import { Inject, Injectable, Optional } from '@angular/core';
import { getId, isset, sameId } from '@cawita/core-front';
import { AsyncStore } from '@cawita/core-front/state';
import { CWT_APP_CONFIG, CwtAppConfig } from '@mobile/modules/core/core-app.config';
import { Partner, Stay } from '@shared/models';
import { filterOutStay, getActiveStayForPartner, getMaximalDate8, getMinimalDate8, StayMergeResult, upsertInArray } from '../../utils';
import { PartnerAPI, PartnerMetadata } from './api/partner.api';
import { StayAPI } from './api/stay.api';
import { StayMergeService } from './services/stay-merge.service';
import { StayStorage } from './services/stay.storage';
import { STAY_LISTENER, StayListener } from './stay-listener';

export interface StayState {
  userId: string;
  activePartner: Partner;
  activeStay?: Stay;
  allStays: Stay[];
  otherStays: Stay[];
  metadata: PartnerMetadata;
}

@Injectable({ providedIn: 'root' })
export class StayStore extends AsyncStore<StayState> {

  constructor(
    private stayAPI: StayAPI,
    private partnerAPI: PartnerAPI,
    private storage: StayStorage,
    private stayMerge: StayMergeService,
    @Inject(CWT_APP_CONFIG) private config: CwtAppConfig,
    @Inject(STAY_LISTENER) @Optional() private stayListeners: StayListener[]
  ) {
    super({
      userId: null,
      activeStay: null,
      activePartner: null,
      metadata: null,
      allStays: [],
      otherStays: [],
      initialized: false,
      loading: false,
    });
  }

  public canEditStay() {
    return this.supportsDates() || this.supportsLocations();
  }

  public supportsDates() {
    return this.config.stay.useDates;
  }

  public supportsLocations() {
    return this.config.partner.useLocations;
  }

  public async init(userId?: string) {
    const stays = await this._getStays(userId);
    const { activePartner, activeStay, allStays, otherStays, metadata } = await this._getInitialState(stays);
    this.setState(c => ({ ...c, userId, activePartner, activeStay, allStays, otherStays, metadata, initialized: true, loading: false }));
    if(isset(activePartner)) this._emitPartnerActivated(activePartner, activeStay);
    return { activePartner, activeStay };
  }

  public async refresh() {
    const { userId } = this.getValue();
    return await this.init(userId);
  }

  public async clearPartner() {
    const { allStays } = this.getValue();
    this.storage.setLastPartnerId(null);
    this.setState(c => ({ ...c, otherStays: allStays, activePartner: null, activeStay: null, metadata: null, initialized: true, loading: false }));
  }

  public async usePartner(parter: Partner) {
    const { allStays } = this.getValue();
    const activePartner = await this.partnerAPI.getPartner(getId(parter)).toPromise();
    const activeStay = allStays?.find(s => sameId(s.partner, activePartner));
    const metadata = await this.partnerAPI.getMetadata(parter, activeStay).toPromise();
    const otherStays = filterOutStay(allStays, activeStay);
    this.storage.setLastPartnerId(activePartner);
    if(isset(activePartner)) this._emitPartnerActivated(activePartner, activeStay);
    this.setState(c => ({ ...c, otherStays, activePartner, activeStay, metadata, initialized: true, loading: false }));
  }

  public async createStay(partner: Partner, stay: Partial<Stay>) {
    const { allStays } = this.getValue();
    const payload = { ...stay, camping: getId(partner) };
    const supportsDates = this.supportsDates();
    if(!supportsDates) {
      payload.startDate = getMinimalDate8();
      payload.endDate = getMaximalDate8();
    }

    const mergeRes = await this.stayMerge.handleMerge(payload, allStays);
    if(!isset(mergeRes)) return null;

    this.setLoading(true);
    const newStay = await this.stayAPI.create(payload).toPromise();
    await this._onMergeStayResponse(newStay, mergeRes);
    await this.usePartner(partner);
    this.setLoading(false);
    return newStay;
  }

  public async updateStay(stayId: string, stay: Partial<Stay>) {
    const { allStays } = this.getValue();
    const mergeRes = await this.stayMerge.handleMerge(stay, allStays);
    if(!isset(mergeRes)) return null;
    this.setLoading(true);
    const newStay = await this.stayAPI.update(stayId, stay).toPromise();
    await this._onMergeStayResponse(newStay, mergeRes);
    this.setLoading(false);
    return newStay;
  }

  private async _onMergeStayResponse(newStay: Stay, mergeResults: StayMergeResult) {
    if (mergeResults.shouldMerge) return await this._replaceStays(mergeResults.staysToMerge, newStay);
    else return await this._upsertStay(newStay);
  }

  private async _replaceStays(replace: (Stay | Partial<Stay>)[], stay: Stay) {
    const { allStays, activePartner } = this.getValue();
    const withoutReplacedStays = allStays.filter(s => !replace.some(r => sameId(r, s)));
    const withNewStay = upsertInArray(withoutReplacedStays, s => sameId(s, stay), stay);
    return await this._updateState(withNewStay, activePartner);
  }

  private async _upsertStay(stay: Stay) {
    const { allStays, activePartner } = this.getValue();
    const withNewStay = upsertInArray(allStays, s => sameId(s, stay), stay);
    return await this._updateState(withNewStay, activePartner);
  }

  private async _updateState(stays: Stay[], activePartner: Partner) {
    const allStays = stays.sort((a, b) => a.startDate - b.startDate);
    const activeStay = getActiveStayForPartner(allStays, getId(activePartner));
    if(isset(activePartner)) this._emitPartnerActivated(activePartner, activeStay);
    const otherStays = filterOutStay(allStays, activeStay);
    this.setState(c => ({ ...c, activePartner, activeStay: activeStay, allStays: allStays, otherStays: otherStays, }));
    return { activeStay, activePartner };
  }

  private async _getInitialState(stays: Stay[]) {
    const cachedPartnerForOffline = this.storage.getLastPartnerId();
    return await this._getNewState(stays, cachedPartnerForOffline);
  }

  private async _getNewState(stays: Stay[], partnerId?: string) {
    const allStays = stays.sort((a, b) => a.startDate - b.startDate);
    const activePartner = partnerId ? await this.partnerAPI.getPartner(partnerId).toPromise() : null;
    const activeStay = getActiveStayForPartner(allStays, getId(activePartner));
    const metadata = activePartner ? await this.partnerAPI.getMetadata(activePartner, activeStay).toPromise() : null;
    const otherStays = filterOutStay(allStays, activeStay);
    return { allStays, activePartner, activeStay, otherStays, metadata };
  }

  private _emitPartnerActivated(partner: Partner, stay?: Stay) {
    if (!this.stayListeners?.length) return;
    this.stayListeners.forEach(listener => listener?.onPartnerActivated?.(partner, stay ?? null));
  }

  private _getStays(userId?: string) {
    if(!isset(userId)) return Promise.resolve([]);
    return this.stayAPI.getNonExpired().toPromise();
  }
}
